import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type CelebrationOptions = {
  durationMs?: number;
  message?: string;
};

type CelebrationContextValue = {
  triggerCelebration: (options?: CelebrationOptions) => void;
};

const CelebrationContext = createContext<CelebrationContextValue | null>(null);

const confettiPieces = Array.from({ length: 36 }, (_, index) => ({
  id: index,
  left: `${3 + (index % 12) * 8}%`,
  delay: `${(index % 8) * 0.05}s`,
  duration: `${1.4 + (index % 6) * 0.12}s`,
  rotate: `${-32 + (index % 9) * 8}deg`,
  colorClass: [
    'bg-emerald-400',
    'bg-amber-300',
    'bg-teal-300',
    'bg-lime-300',
    'bg-stone-900',
  ][index % 5],
}));

export function CelebrationProvider({ children }: { children: ReactNode }) {
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState('Excellent work');
  const [durationMs, setDurationMs] = useState(2000);

  const triggerCelebration = useCallback((options?: CelebrationOptions) => {
    setMessage(options?.message ?? 'Excellent work');
    setDurationMs(options?.durationMs ?? 2000);
    setIsVisible(false);

    window.requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setIsVisible(false);
    }, durationMs);

    return () => window.clearTimeout(timeout);
  }, [durationMs, isVisible]);

  const value = useMemo(
    () => ({
      triggerCelebration,
    }),
    [triggerCelebration],
  );

  return (
    <CelebrationContext.Provider value={value}>
      {children}
      {isVisible ? <CelebrationOverlay message={message} /> : null}
    </CelebrationContext.Provider>
  );
}

function CelebrationOverlay({ message }: { message: string }) {
  return (
    <div className="pointer-events-none fixed inset-0 z-[140] overflow-hidden">
      <div className="celebration-screen absolute inset-0 bg-stone-950/18 backdrop-blur-[1px]" />
      {confettiPieces.map((piece) => (
        <span
          key={piece.id}
          className={`celebration-confetti absolute top-[-12%] h-4 w-2 rounded-full ${piece.colorClass}`}
          style={{
            left: piece.left,
            animationDelay: piece.delay,
            animationDuration: piece.duration,
            transform: `rotate(${piece.rotate})`,
          }}
        />
      ))}
      <div className="absolute inset-0 flex items-center justify-center px-6">
        <div className="celebration-message rounded-full border border-white/70 bg-white/85 px-6 py-3 text-center shadow-[0_20px_50px_rgba(28,25,23,0.16)]">
          <p className="text-2xl font-semibold tracking-tight text-stone-950 sm:text-3xl">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
}

export function useCelebration() {
  const context = useContext(CelebrationContext);

  if (!context) {
    throw new Error('useCelebration must be used within CelebrationProvider');
  }

  return context;
}
